import React, { useEffect, useState, useRef } from "react";
import { Loader2, Save, RotateCcw, Upload, Image as ImageIcon, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api, formatErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const fullUrl = (u) => (u && typeof u === "string" && u.startsWith("/api/") ? `${BACKEND}${u}` : u);

// Schema = visible label + group + heuristic for input type
const PAGE_SCHEMAS = {
  home: [
    { group: "Hero", fields: [
      { key: "hero_eyebrow", label: "Hero eyebrow" },
      { key: "hero_title", label: "Hero title (line 1)" },
      { key: "hero_title_italic", label: "Hero title — italic highlight" },
      { key: "hero_subtitle", label: "Hero subtitle", type: "textarea" },
      { key: "hero_image", label: "Hero background image", type: "image" },
      { key: "hero_cta_primary", label: "Primary CTA button text" },
      { key: "hero_cta_secondary", label: "Secondary CTA button text" },
    ]},
    { group: "Stats (4 numbers in hero strip)", fields: [
      { key: "stat_1_num", label: "Stat 1 — number" }, { key: "stat_1_label", label: "Stat 1 — label" },
      { key: "stat_2_num", label: "Stat 2 — number" }, { key: "stat_2_label", label: "Stat 2 — label" },
      { key: "stat_3_num", label: "Stat 3 — number" }, { key: "stat_3_label", label: "Stat 3 — label" },
      { key: "stat_4_num", label: "Stat 4 — number" }, { key: "stat_4_label", label: "Stat 4 — label" },
    ]},
    { group: "Why TTI Holidays (value section)", fields: [
      { key: "value_eyebrow", label: "Section eyebrow" },
      { key: "value_title", label: "Section title" },
      { key: "value_title_italic", label: "Section title — italic highlight" },
      { key: "value_intro", label: "Section intro paragraph", type: "textarea" },
      { key: "value_1_title", label: "Pillar 1 — title" }, { key: "value_1_text", label: "Pillar 1 — text", type: "textarea" },
      { key: "value_2_title", label: "Pillar 2 — title" }, { key: "value_2_text", label: "Pillar 2 — text", type: "textarea" },
      { key: "value_3_title", label: "Pillar 3 — title" }, { key: "value_3_text", label: "Pillar 3 — text", type: "textarea" },
      { key: "value_4_title", label: "Pillar 4 — title" }, { key: "value_4_text", label: "Pillar 4 — text", type: "textarea" },
    ]},
    { group: "Top destinations section heading", fields: [
      { key: "destinations_eyebrow", label: "Eyebrow" },
      { key: "destinations_title", label: "Title (before italic)" },
      { key: "destinations_title_italic", label: "Title — italic highlight" },
      { key: "destinations_title_after", label: "Title (after italic)" },
    ]},
    { group: "Signature journeys section heading", fields: [
      { key: "tours_eyebrow", label: "Eyebrow" },
      { key: "tours_title", label: "Title" },
      { key: "tours_title_italic", label: "Title — italic highlight" },
    ]},
    { group: "Extensions section", fields: [
      { key: "extensions_eyebrow", label: "Eyebrow" },
      { key: "extensions_title", label: "Title" },
      { key: "extensions_title_italic", label: "Title — italic highlight" },
      { key: "extensions_body", label: "Body paragraph", type: "textarea" },
    ]},
    { group: "Our promise section", fields: [
      { key: "promise_eyebrow", label: "Eyebrow" },
      { key: "promise_title", label: "Title" },
      { key: "promise_title_italic", label: "Title — italic highlight" },
    ]},
    { group: "Testimonials section heading", fields: [
      { key: "testimonials_eyebrow", label: "Eyebrow" },
      { key: "testimonials_title", label: "Title" },
      { key: "testimonials_title_italic", label: "Title — italic highlight" },
      { key: "testimonials_rating_count", label: "Reviews count display (e.g. 520+ reviews)" },
    ]},
    { group: "Bottom CTA band", fields: [
      { key: "cta_eyebrow", label: "Eyebrow" },
      { key: "cta_title", label: "Title (before italic)" },
      { key: "cta_title_italic", label: "Title — italic highlight" },
      { key: "cta_title_after", label: "Title (after italic)" },
      { key: "cta_body", label: "Body line", type: "textarea" },
      { key: "cta_image", label: "CTA background image", type: "image" },
    ]},
  ],
  about: [
    { group: "Hero", fields: [
      { key: "hero_eyebrow", label: "Hero eyebrow" },
      { key: "hero_title", label: "Hero title" },
      { key: "hero_subtitle", label: "Hero subtitle", type: "textarea" },
      { key: "hero_image", label: "Hero background image", type: "image" },
    ]},
    { group: "Our story", fields: [
      { key: "story_eyebrow", label: "Eyebrow" },
      { key: "story_title", label: "Title (before italic)" },
      { key: "story_title_italic", label: "Title — italic highlight" },
      { key: "story_para_1", label: "Paragraph 1", type: "textarea" },
      { key: "story_para_2", label: "Paragraph 2", type: "textarea" },
      { key: "story_para_3", label: "Paragraph 3", type: "textarea" },
      { key: "story_signature", label: "Signature line" },
      { key: "story_location", label: "Signature location" },
    ]},
    { group: "Our values (4 cards)", fields: [
      { key: "values_eyebrow", label: "Section eyebrow" },
      { key: "values_title", label: "Section title" },
      { key: "value_1_title", label: "Value 1 — title" }, { key: "value_1_text", label: "Value 1 — text", type: "textarea" },
      { key: "value_2_title", label: "Value 2 — title" }, { key: "value_2_text", label: "Value 2 — text", type: "textarea" },
      { key: "value_3_title", label: "Value 3 — title" }, { key: "value_3_text", label: "Value 3 — text", type: "textarea" },
      { key: "value_4_title", label: "Value 4 — title" }, { key: "value_4_text", label: "Value 4 — text", type: "textarea" },
    ]},
    { group: "Numbers strip", fields: [
      { key: "numbers_eyebrow", label: "Eyebrow" },
      { key: "numbers_title", label: "Title (before italic)" },
      { key: "numbers_title_italic", label: "Title — italic highlight" },
      { key: "stat_1_num", label: "Stat 1 — number" }, { key: "stat_1_label", label: "Stat 1 — label" },
      { key: "stat_2_num", label: "Stat 2 — number" }, { key: "stat_2_label", label: "Stat 2 — label" },
      { key: "stat_3_num", label: "Stat 3 — number" }, { key: "stat_3_label", label: "Stat 3 — label" },
      { key: "stat_4_num", label: "Stat 4 — number" }, { key: "stat_4_label", label: "Stat 4 — label" },
    ]},
  ],
  services: [
    { group: "Hero", fields: [
      { key: "hero_eyebrow", label: "Hero eyebrow" },
      { key: "hero_title", label: "Hero title" },
      { key: "hero_subtitle", label: "Hero subtitle", type: "textarea" },
      { key: "hero_image", label: "Hero background image", type: "image" },
    ]},
    { group: "Intro", fields: [
      { key: "intro_eyebrow", label: "Eyebrow" },
      { key: "intro_title", label: "Title" },
      { key: "intro_body", label: "Body", type: "textarea" },
    ]},
  ],
  contact: [
    { group: "Hero", fields: [
      { key: "hero_eyebrow", label: "Hero eyebrow" },
      { key: "hero_title", label: "Hero title" },
      { key: "hero_subtitle", label: "Hero subtitle", type: "textarea" },
      { key: "hero_image", label: "Hero background image", type: "image" },
    ]},
    { group: "Intro block", fields: [
      { key: "intro_eyebrow", label: "Eyebrow" },
      { key: "intro_title", label: "Title (before italic)" },
      { key: "intro_title_italic", label: "Title — italic highlight" },
      { key: "intro_body", label: "Intro body", type: "textarea" },
    ]},
    { group: "Office address", fields: [
      { key: "office_label", label: "Label" },
      { key: "office_address_line_1", label: "Address line 1" },
      { key: "office_address_line_2", label: "Address line 2" },
    ]},
    { group: "Phone / WhatsApp", fields: [
      { key: "phone_label", label: "Label" },
      { key: "phone_value", label: "Phone number" },
    ]},
    { group: "Email", fields: [
      { key: "email_label", label: "Label" },
      { key: "email_value", label: "Email address" },
    ]},
    { group: "Office hours", fields: [
      { key: "hours_label", label: "Label" },
      { key: "hours_value", label: "Hours" },
    ]},
  ],
  tours: [
    { group: "Page hero", fields: [
      { key: "hero_eyebrow", label: "Hero eyebrow" },
      { key: "hero_title", label: "Hero title" },
      { key: "hero_subtitle", label: "Hero subtitle", type: "textarea" },
      { key: "hero_image", label: "Hero background image", type: "image" },
    ]},
  ],
  destinations: [
    { group: "Page hero", fields: [
      { key: "hero_eyebrow", label: "Hero eyebrow" },
      { key: "hero_title", label: "Hero title" },
      { key: "hero_subtitle", label: "Hero subtitle", type: "textarea" },
      { key: "hero_image", label: "Hero background image", type: "image" },
    ]},
  ],
  testimonials: [
    { group: "Page hero", fields: [
      { key: "hero_eyebrow", label: "Hero eyebrow" },
      { key: "hero_title", label: "Hero title" },
      { key: "hero_subtitle", label: "Hero subtitle", type: "textarea" },
      { key: "hero_image", label: "Hero background image", type: "image" },
    ]},
  ],
};

const PAGE_LABELS = {
  home: "Home", about: "About", services: "Services", contact: "Contact",
  tours: "Tours (list page)", destinations: "Destinations (list page)", testimonials: "Reviews / Testimonials",
};

function ImageField({ value, onChange, label, testid }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const uploadFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(res.data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.15em] text-foreground/70">{label}</Label>
      <div className="flex items-stretch gap-3 mt-1.5">
        <div className="flex-1 space-y-1.5">
          <Input
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https://… or /api/files/…"
            data-testid={testid}
          />
          <div className="flex items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              onChange={uploadFile}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-1.5 px-3 py-1 text-[11px] uppercase tracking-wider bg-peacock hover:bg-deepred text-white rounded-sm transition-colors disabled:opacity-50"
              data-testid={`${testid}-upload-btn`}
            >
              {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
              Upload new image
            </button>
            <span className="text-[10px] text-foreground/55">jpg/png/webp · max 8 MB</span>
          </div>
        </div>
        {value && (
          <div className="w-24 h-16 bg-cream-100 border border-cream-200 rounded-sm overflow-hidden flex-shrink-0">
            <img src={fullUrl(value)} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          </div>
        )}
      </div>
    </div>
  );
}

function PageEditor({ page }) {
  const [content, setContent] = useState({});
  const [original, setOriginal] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openGroups, setOpenGroups] = useState({ 0: true }); // first group open by default

  const schema = PAGE_SCHEMAS[page] || [];

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/site-content/${page}`);
      setContent(r.data.content || {});
      setOriginal(r.data.content || {});
    } catch (e) {
      toast.error("Failed to load page content");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [page]);

  const setField = (k, v) => setContent((prev) => ({ ...prev, [k]: v }));
  const dirty = JSON.stringify(content) !== JSON.stringify(original);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/site-content/${page}`, { content });
      setOriginal(content);
      toast.success(`${PAGE_LABELS[page]} page updated`);
    } catch (e) {
      toast.error(formatErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm(`Reset ${PAGE_LABELS[page]} page to default content? This cannot be undone.`)) return;
    setSaving(true);
    try {
      const r = await api.post(`/admin/site-content/${page}/reset`);
      setContent(r.data.content || {});
      setOriginal(r.data.content || {});
      toast.success("Reset to defaults");
    } catch (e) {
      toast.error("Reset failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleGroup = (idx) => setOpenGroups((s) => ({ ...s, [idx]: !s[idx] }));

  if (loading) {
    return (
      <div className="bg-white border border-cream-200 p-12 text-center">
        <Loader2 className="w-5 h-5 animate-spin inline text-saffron" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid={`page-editor-${page}`}>
      {schema.map((group, gIdx) => (
        <div key={gIdx} className="bg-white border border-cream-200 rounded-sm overflow-hidden">
          <button
            type="button"
            onClick={() => toggleGroup(gIdx)}
            className="w-full px-5 py-4 flex items-center justify-between hover:bg-cream-50 transition-colors text-left"
            data-testid={`group-toggle-${page}-${gIdx}`}
          >
            <span className="font-serif text-lg text-deepred">{group.group}</span>
            <ChevronDown className={`w-4 h-4 text-foreground/50 transition-transform ${openGroups[gIdx] ? "rotate-180" : ""}`} />
          </button>
          {openGroups[gIdx] && (
            <div className="p-5 border-t border-cream-200 space-y-4 bg-cream-50/40">
              {group.fields.map((f) => {
                const testid = `field-${page}-${f.key}`;
                if (f.type === "image") {
                  return (
                    <ImageField
                      key={f.key}
                      label={f.label}
                      value={content[f.key]}
                      onChange={(v) => setField(f.key, v)}
                      testid={testid}
                    />
                  );
                }
                if (f.type === "textarea") {
                  return (
                    <div key={f.key}>
                      <Label className="text-xs uppercase tracking-[0.15em] text-foreground/70">{f.label}</Label>
                      <Textarea
                        rows={3}
                        value={content[f.key] || ""}
                        onChange={(e) => setField(f.key, e.target.value)}
                        data-testid={testid}
                      />
                    </div>
                  );
                }
                return (
                  <div key={f.key}>
                    <Label className="text-xs uppercase tracking-[0.15em] text-foreground/70">{f.label}</Label>
                    <Input
                      value={content[f.key] || ""}
                      onChange={(e) => setField(f.key, e.target.value)}
                      data-testid={testid}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}

      {/* sticky action bar */}
      <div className="sticky bottom-0 z-10 bg-white border border-cream-300 rounded-sm shadow-md px-5 py-3 flex items-center justify-between">
        <div className="text-xs text-foreground/65">
          {dirty ? <span className="text-deepred font-medium">Unsaved changes</span> : "All changes saved"}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleReset}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-xs uppercase tracking-wider border border-cream-300 hover:bg-cream-100 rounded-sm transition-colors disabled:opacity-50"
            data-testid={`reset-${page}`}
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Reset to defaults
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!dirty || saving}
            className="inline-flex items-center gap-1.5 px-5 py-2 bg-saffron hover:bg-deepred disabled:opacity-40 text-white text-xs uppercase tracking-wider rounded-sm transition-colors"
            data-testid={`save-${page}`}
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save {PAGE_LABELS[page]}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminPagesEditor() {
  const [activePage, setActivePage] = useState("home");
  const pages = Object.keys(PAGE_SCHEMAS);

  return (
    <div className="space-y-4" data-testid="pages-editor-panel">
      <div className="bg-gradient-to-r from-saffron/10 via-marigold/10 to-peacock/10 border border-saffron/30 rounded-sm p-5">
        <h2 className="font-serif text-xl text-deepred mb-1 flex items-center gap-2">
          <ImageIcon className="w-5 h-5" />
          Website content editor
        </h2>
        <p className="text-sm text-foreground/75">
          Edit every page&apos;s text and images live. Changes take effect immediately on the public site.
        </p>
      </div>

      {/* Page selector tabs */}
      <div className="bg-white border border-cream-200 rounded-sm overflow-x-auto">
        <div className="flex" role="tablist">
          {pages.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setActivePage(p)}
              role="tab"
              data-testid={`page-tab-${p}`}
              className={`flex-shrink-0 px-5 py-3 text-sm uppercase tracking-wider transition-colors border-b-2 ${
                activePage === p ? "border-saffron text-saffron font-semibold bg-cream-50" : "border-transparent text-foreground/65 hover:text-foreground"
              }`}
            >
              {PAGE_LABELS[p]}
            </button>
          ))}
        </div>
      </div>

      <PageEditor key={activePage} page={activePage} />
    </div>
  );
}
